import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { pm } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';
import { MentionInput, CommentBody } from '../../components/common/MentionInput';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

// ─── Meta ─────────────────────────────────────────────────────────────────────
const SEV = {
  blocker:  { label: 'Blocker',  color: '#fff',    bg: '#18181B', border: '#18181B', dot: '#18181B',  sla: '4h' },
  critical: { label: 'Critical', color: '#9F1239', bg: '#FFF1F2', border: '#FECDD3', dot: '#F43F5E',  sla: '4h' },
  major:    { label: 'Major',    color: '#9A3412', bg: '#FFF7ED', border: '#FED7AA', dot: '#F97316',  sla: '24h' },
  minor:    { label: 'Minor',    color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', dot: '#F59E0B',  sla: '3d' },
  trivial:  { label: 'Trivial',  color: '#374151', bg: '#F3F4F6', border: '#E5E7EB', dot: '#9CA3AF',  sla: '7d' },
};
const PRI = {
  critical: { label: 'P0 — Critical', color: '#DC2626', bg: '#FEF2F2', icon: '🔴' },
  high:     { label: 'P1 — High',     color: '#EA580C', bg: '#FFF7ED', icon: '🟠' },
  medium:   { label: 'P2 — Medium',   color: '#D97706', bg: '#FFFBEB', icon: '🟡' },
  low:      { label: 'P3 — Low',      color: '#16A34A', bg: '#F0FDF4', icon: '🟢' },
};
const ST = {
  new:          { label: 'New',          color: '#6B7280', bg: '#F3F4F6', icon: '◌' },
  assigned:     { label: 'Assigned',     color: '#2563EB', bg: '#EFF6FF', icon: '👤' },
  in_progress:  { label: 'In Progress',  color: '#EA580C', bg: '#FFF7ED', icon: '⚡' },
  fixed:        { label: 'Fixed',        color: '#0891B2', bg: '#ECFEFF', icon: '🔧' },
  code_review:  { label: 'Code Review',  color: '#7C3AED', bg: '#F5F3FF', icon: '🔍' },
  qa_testing:   { label: 'QA Testing',   color: '#BE185D', bg: '#FDF2F8', icon: '🧪' },
  uat:          { label: 'UAT',          color: '#0D9488', bg: '#F0FDFA', icon: '✅' },
  closed:       { label: 'Closed',       color: '#16A34A', bg: '#F0FDF4', icon: '●' },
  reopened:     { label: 'Reopened',     color: '#DC2626', bg: '#FFF1F2', icon: '🔁' },
};
const ENVS       = ['production', 'uat', 'staging', 'development'];
const ROOT_CAUSES = ['requirement_gap','design_issue','coding_defect','integration_issue','data_issue'];
const TEMPLATES  = [
  { key: 'general',         label: '🐛 General Bug' },
  { key: 'ui_bug',          label: '🎨 UI Bug' },
  { key: 'performance_bug', label: '⚡ Performance Bug' },
  { key: 'api_bug',         label: '🔌 API Bug' },
];
const TEMPLATE_DEFAULTS = {
  ui_bug: {
    steps_to_reproduce: '1. Navigate to the page\n2. Observe the UI element\n3. Compare with design spec',
    expected_result: 'UI should match the design specification',
    actual_result: 'UI element is misaligned / incorrect',
  },
  performance_bug: {
    steps_to_reproduce: '1. Open the page/feature\n2. Perform the action\n3. Observe response time',
    expected_result: 'Response time < 2 seconds',
    actual_result: 'Response time > X seconds',
  },
  api_bug: {
    steps_to_reproduce: '1. Call the API endpoint\n2. Pass request payload\n3. Observe response',
    expected_result: 'API returns 200 with correct data',
    actual_result: 'API returns unexpected status / data',
  },
};

const SEV_ORDER  = ['blocker','critical','major','minor','trivial'];
const OPEN_STATUSES = ['new','assigned','in_progress','fixed','code_review','qa_testing','uat','reopened'];
const PALETTE = ['#F43F5E','#F97316','#F59E0B','#3B82F6','#8B5CF6','#14B8A6','#22C55E','#6B7280'];

function fmt(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—'; }
function fmtTime(d) { return d ? new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'; }

function SlaChip({ sla_remaining_min, sla_breached, status }) {
  if (status === 'closed' || (!sla_remaining_min && !sla_breached)) return null;
  if (sla_breached || sla_remaining_min < 0) {
    return <span style={{ background:'#FEF2F2', color:'#DC2626', padding:'1px 7px', borderRadius:9999, fontSize:'0.68rem', fontWeight:700 }}>⚠ SLA Breached</span>;
  }
  const hrs = Math.floor(sla_remaining_min / 60);
  const label = hrs >= 24 ? `${Math.floor(hrs/24)}d left` : hrs > 0 ? `${hrs}h left` : `${sla_remaining_min}m left`;
  const color = sla_remaining_min < 60 ? '#DC2626' : sla_remaining_min < 240 ? '#D97706' : '#6B7280';
  return <span style={{ background:'#F3F4F6', color, padding:'1px 7px', borderRadius:9999, fontSize:'0.68rem', fontWeight:600 }}>⏱ {label}</span>;
}

// ─── Bug Form Modal ────────────────────────────────────────────────────────────
function BugModal({ bug, projectId, members, onClose, onSaved }) {
  const isNew = !bug;
  const [form, setForm] = useState(bug ? {
    title: bug.title, description: bug.description || '',
    steps_to_reproduce: bug.steps_to_reproduce || '',
    expected_result: bug.expected_result || '', actual_result: bug.actual_result || '',
    severity: bug.severity || 'major', priority: bug.priority || 'high',
    status: bug.status || 'new', environment: bug.environment || '',
    browser_device: bug.browser_device || '', build_version: bug.build_version || '',
    root_cause: bug.root_cause || '', is_regression: bug.is_regression || false,
    fix_version: bug.fix_version || '', assigned_to: bug.assigned_to || '',
    qa_assignee_id: bug.qa_assignee_id || '', template_type: bug.template_type || 'general',
    duplicate_of_id: bug.duplicate_of_id || '',
    pm_story_id: bug.pm_story_id || '', module_id: '',
  } : { title:'', description:'', steps_to_reproduce:'', expected_result:'', actual_result:'',
        severity:'major', priority:'high', status:'new', environment:'', browser_device:'',
        build_version:'', root_cause:'', is_regression:false, fix_version:'', assigned_to:'',
        qa_assignee_id:'', template_type:'general', duplicate_of_id:'',
        pm_story_id: '', module_id: '' });

  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic');
  const [modules, setModules] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    Promise.all([
      pm.getModules(projectId),
      pm.getIssues(projectId, { no_parent: true }),
    ]).then(([mRes, iRes]) => {
      setModules(mRes.data || []);
      setStories(iRes.data || []);
    }).catch(() => {});
  }, [projectId]);

  const filteredStories = form.module_id
    ? stories.filter(s => s.module_ids?.includes(Number(form.module_id)))
    : stories;

  const sm = SEV[form.severity] || SEV.major;
  const st = ST[form.status]    || ST.new;

  const applyTemplate = (key) => {
    const d = TEMPLATE_DEFAULTS[key] || {};
    setForm(f => ({ ...f, template_type: key, ...d }));
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const data = { ...form,
        assigned_to:    form.assigned_to    ? Number(form.assigned_to)    : null,
        qa_assignee_id: form.qa_assignee_id ? Number(form.qa_assignee_id) : null,
        duplicate_of_id: form.duplicate_of_id ? Number(form.duplicate_of_id) : null,
        pm_story_id: form.pm_story_id ? Number(form.pm_story_id) : null,
        module_id:   form.module_id   ? Number(form.module_id)   : null,
      };
      if (isNew) await pm.createBug(projectId, data);
      else await pm.updateBug(bug.id, data);
      onSaved(); onClose();
    } catch (err) { alert(err.response?.data?.detail || 'Failed.'); }
    setSaving(false);
  };

  const tabStyle = (t) => ({
    padding: '0.4rem 0.9rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
    background: tab === t ? '#fff' : 'transparent',
    color: tab === t ? sm.dot || theme.primary : '#6B7280',
    borderBottom: tab === t ? `2px solid ${sm.dot || theme.primary}` : '2px solid transparent',
    transition: 'all 0.12s',
  });
  const inp = { padding:'0.45rem 0.75rem', border:`1px solid #E5E7EB`, borderRadius:8, fontSize:'0.875rem',
                background:'#fff', boxSizing:'border-box', width:'100%', outline:'none' };
  const lbl = { display:'block', fontSize:'0.72rem', fontWeight:700, color:'#6B7280',
                textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:3 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', backdropFilter:'blur(4px)',
                  display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:680,
                    boxShadow:'0 24px 64px rgba(0,0,0,0.2)', maxHeight:'92vh', overflowY:'auto' }}>

        {/* Header */}
        <div style={{ padding:'1rem 1.5rem', background:`linear-gradient(135deg, ${sm.bg}, #fff)`,
                      borderBottom:`1px solid #F1F5F9`, borderRadius:'16px 16px 0 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
              <span style={{ fontSize:'1.3rem' }}>⬡</span>
              <div>
                <h3 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:'#111827' }}>
                  {isNew ? 'Report a Bug' : `Edit Bug #${bug.id}`}
                </h3>
                <p style={{ margin:0, fontSize:'0.72rem', color:'#6B7280' }}>
                  {isNew ? 'Capture all details for fast resolution' : bug.title}
                </p>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8,
              width:32, height:32, cursor:'pointer', color:'#6B7280', fontSize:'1rem',
              display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
          </div>

          {/* Template selector (new only) */}
          {isNew && (
            <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.75rem', flexWrap:'wrap' }}>
              {TEMPLATES.map(t => (
                <button key={t.key} type="button" onClick={() => applyTemplate(t.key)}
                  style={{ padding:'0.25rem 0.65rem', borderRadius:9999, fontSize:'0.72rem', fontWeight:600,
                           border:`1px solid ${form.template_type===t.key ? sm.dot : '#E5E7EB'}`,
                           background: form.template_type===t.key ? sm.bg : '#F9FAFB',
                           color: form.template_type===t.key ? sm.color : '#6B7280', cursor:'pointer' }}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sub-tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #F1F5F9', padding:'0 1.5rem', background:'#FAFAFA' }}>
          {[['basic','📋 Basic'],['repro','🔁 Reproduce'],['classify','🏷 Classify'],['assign','👥 Assign']].map(([k,l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} style={tabStyle(k)}>{l}</button>
          ))}
        </div>

        <form onSubmit={save} style={{ padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'1rem' }}>

          {/* ── Basic Tab ── */}
          {tab === 'basic' && (
            <>
              <div>
                <label style={lbl}>Title *</label>
                <input required value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))}
                  style={inp} placeholder="Brief description of the bug…" autoFocus />
              </div>
              <div>
                <label style={lbl}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))}
                  rows={3} style={{...inp, resize:'vertical'}} placeholder="What happened?" />
              </div>

              {/* Module & Story linkage */}
              {isNew ? (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  <div>
                    <label style={lbl}>Module (optional)</label>
                    <select value={form.module_id || ''} style={inp}
                      onChange={e => setForm(f => ({ ...f, module_id: e.target.value, pm_story_id: '' }))}>
                      <option value="">— All Modules —</option>
                      {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Story / Issue (optional)</label>
                    <select value={form.pm_story_id || ''} style={inp}
                      onChange={e => setForm(f => ({ ...f, pm_story_id: e.target.value }))}>
                      <option value="">— Not linked —</option>
                      {filteredStories.map(s => (
                        <option key={s.id} value={s.id}>{s.code} — {s.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : bug?.pm_story_title ? (
                <div style={{ padding:'0.45rem 0.75rem', background:'#EFF6FF', borderRadius:8,
                              fontSize:'0.82rem', color:'#1D4ED8', fontWeight:500 }}>
                  📎 Linked Story: <strong>{bug.pm_story_title}</strong>
                </div>
              ) : null}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label style={lbl}>Environment</label>
                  <select value={form.environment} onChange={e => setForm(f=>({...f,environment:e.target.value}))} style={inp}>
                    <option value="">— Select —</option>
                    {ENVS.map(e => <option key={e} value={e}>{e.charAt(0).toUpperCase()+e.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Build Version</label>
                  <input value={form.build_version} onChange={e => setForm(f=>({...f,build_version:e.target.value}))}
                    style={inp} placeholder="e.g. v1.4.2" />
                </div>
                <div>
                  <label style={lbl}>Browser / Device</label>
                  <input value={form.browser_device} onChange={e => setForm(f=>({...f,browser_device:e.target.value}))}
                    style={inp} placeholder="e.g. Chrome 120 / iPhone 15" />
                </div>
                <div>
                  <label style={lbl}>Fix Version</label>
                  <input value={form.fix_version} onChange={e => setForm(f=>({...f,fix_version:e.target.value}))}
                    style={inp} placeholder="e.g. v1.5.0" />
                </div>
              </div>
              <div style={{ display:'flex', gap:'0.75rem', alignItems:'center' }}>
                <label style={{ display:'flex', alignItems:'center', gap:'0.4rem', cursor:'pointer', fontSize:'0.85rem', fontWeight:500 }}>
                  <input type="checkbox" checked={form.is_regression}
                    onChange={e => setForm(f=>({...f,is_regression:e.target.checked}))}
                    style={{ width:14, height:14 }} />
                  <span>🔁 Regression Bug</span>
                </label>
              </div>
            </>
          )}

          {/* ── Reproduce Tab ── */}
          {tab === 'repro' && (
            <>
              <div>
                <label style={lbl}>Steps to Reproduce *</label>
                <textarea value={form.steps_to_reproduce}
                  onChange={e => setForm(f=>({...f,steps_to_reproduce:e.target.value}))}
                  rows={5} style={{...inp, resize:'vertical', fontFamily:'monospace', fontSize:'0.82rem'}}
                  placeholder="1. Go to…&#10;2. Click on…&#10;3. Observe error" />
              </div>
              <div>
                <label style={lbl}>Expected Result</label>
                <textarea value={form.expected_result} onChange={e => setForm(f=>({...f,expected_result:e.target.value}))}
                  rows={3} style={{...inp, resize:'vertical'}} placeholder="What should happen?" />
              </div>
              <div>
                <label style={lbl}>Actual Result</label>
                <textarea value={form.actual_result} onChange={e => setForm(f=>({...f,actual_result:e.target.value}))}
                  rows={3} style={{...inp, resize:'vertical', borderColor:'#FECACA'}}
                  placeholder="What actually happened?" />
              </div>
            </>
          )}

          {/* ── Classify Tab ── */}
          {tab === 'classify' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label style={lbl}>Severity</label>
                  <select value={form.severity} onChange={e => setForm(f=>({...f,severity:e.target.value}))} style={{...inp, background: SEV[form.severity]?.bg}}>
                    {SEV_ORDER.map(s => <option key={s} value={s}>{SEV[s]?.label} · {SEV[s]?.sla}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f=>({...f,priority:e.target.value}))} style={inp}>
                    {Object.entries(PRI).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Status</label>
                  <select value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))} style={inp}>
                    {Object.entries(ST).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Root Cause</label>
                <select value={form.root_cause} onChange={e => setForm(f=>({...f,root_cause:e.target.value}))} style={inp}>
                  <option value="">— Not identified yet —</option>
                  {ROOT_CAUSES.map(r => <option key={r} value={r}>{r.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                </select>
              </div>
              {/* Preview */}
              <div style={{ padding:'0.75rem 1rem', background:sm.bg, borderRadius:10, border:`1px solid ${sm.border}`,
                             display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:sm.dot }} />
                <span style={{ fontSize:'0.82rem', fontWeight:700, color:sm.color }}>{sm.label} severity</span>
                <span style={{ fontSize:'0.72rem', color:'#6B7280' }}>SLA: {sm.sla} · {PRI[form.priority]?.icon} {PRI[form.priority]?.label}</span>
                <span style={{ marginLeft:'auto', background:st.bg, color:st.color, padding:'2px 8px', borderRadius:9999, fontSize:'0.72rem', fontWeight:600 }}>{st.icon} {st.label}</span>
              </div>
            </>
          )}

          {/* ── Assign Tab ── */}
          {tab === 'assign' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label style={lbl}>Assigned To (Developer)</label>
                  <select value={form.assigned_to} onChange={e => setForm(f=>({...f,assigned_to:e.target.value}))} style={inp}>
                    <option value="">— Unassigned —</option>
                    {members.map(m => <option key={m.employee_id} value={m.employee_id}>{m.employee_name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>QA Assignee</label>
                  <select value={form.qa_assignee_id} onChange={e => setForm(f=>({...f,qa_assignee_id:e.target.value}))} style={inp}>
                    <option value="">— Unassigned —</option>
                    {members.map(m => <option key={m.employee_id} value={m.employee_id}>{m.employee_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Duplicate Of (Bug ID)</label>
                <input type="number" value={form.duplicate_of_id}
                  onChange={e => setForm(f=>({...f,duplicate_of_id:e.target.value}))}
                  style={inp} placeholder="Enter Bug ID if this is a duplicate…" />
              </div>
            </>
          )}

          <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', paddingTop:'0.5rem', borderTop:`1px solid #F1F5F9` }}>
            <button type="button" onClick={onClose}
              style={{ padding:'0.5rem 1.125rem', border:`1px solid #E5E7EB`, background:'#fff', borderRadius:9,
                       cursor:'pointer', fontWeight:600, fontSize:'0.825rem', color:'#374151' }}>Cancel</button>
            <button type="submit" disabled={saving}
              style={{ padding:'0.5rem 1.375rem', background: saving ? '#FDA4AF' : '#F43F5E', color:'#fff', border:'none',
                       borderRadius:9, cursor: saving ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:'0.825rem' }}>
              {saving ? '⟳ Saving…' : isNew ? '⬡ Report Bug' : '✓ Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Bug Detail Drawer ────────────────────────────────────────────────────────
function BugDrawer({ bug, members, onClose, onUpdated }) {
  const [detail, setDetail]     = useState(bug);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [comment, setComment]   = useState('');
  const [tab, setTab]           = useState('details');
  const [editing, setEditing]   = useState(false);

  const load = useCallback(async () => {
    const [bRes, cRes, aRes] = await Promise.all([
      pm.getBug(bug.id), pm.getBugComments(bug.id), pm.getBugActivities(bug.id),
    ]);
    setDetail(bRes.data);
    setComments(cRes.data || []);
    setActivities(aRes.data || []);
  }, [bug.id]);

  useEffect(() => { load(); }, [load]);

  const addComment = async () => {
    if (!comment.trim()) return;
    await pm.addBugComment(bug.id, comment);
    setComment('');
    load();
  };

  const changeStatus = async (status) => {
    if (status === 'reopened') { await pm.reopenBug(bug.id); }
    else { await pm.updateBug(bug.id, { status }); }
    load(); onUpdated();
  };

  const sm = SEV[detail.severity]   || SEV.major;
  const st = ST[detail.status]      || ST.new;
  const pm_ = PRI[detail.priority]  || PRI.medium;

  const inp = { padding:'0.45rem 0.75rem', border:`1px solid #E5E7EB`, borderRadius:8,
                fontSize:'0.875rem', background:'#fff', outline:'none', width:'100%', boxSizing:'border-box' };

  const NEXT_STATES = {
    new:['assigned','in_progress'], assigned:['in_progress'], in_progress:['fixed'],
    fixed:['code_review','qa_testing'], code_review:['qa_testing','in_progress'],
    qa_testing:['uat','reopened','closed'], uat:['closed','reopened'],
    closed:['reopened'], reopened:['in_progress','assigned'],
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.4)', backdropFilter:'blur(2px)', zIndex:1000, display:'flex', justifyContent:'flex-end' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width:'min(560px, 100vw)', background:'#fff', height:'100%', overflowY:'auto',
                    boxShadow:'-8px 0 32px rgba(0,0,0,0.15)', display:'flex', flexDirection:'column' }}>

        {/* Drawer header */}
        <div style={{ padding:'1rem 1.25rem', background:`linear-gradient(135deg, ${sm.bg}, #fff)`,
                      borderBottom:`1px solid #F1F5F9`, position:'sticky', top:0, zIndex:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'0.35rem' }}>
                <span style={{ background:sm.bg, color:sm.color, border:`1px solid ${sm.border}`, padding:'1px 8px',
                               borderRadius:6, fontSize:'0.7rem', fontWeight:800 }}>{sm.label}</span>
                <span style={{ background:st.bg, color:st.color, padding:'1px 8px', borderRadius:9999, fontSize:'0.7rem', fontWeight:600 }}>{st.icon} {st.label}</span>
                <span style={{ color:pm_.color, fontSize:'0.7rem', fontWeight:700 }}>{pm_.icon} {pm_.label}</span>
                {detail.is_regression && <span style={{ background:'#FEF9C3', color:'#854D0E', padding:'1px 7px', borderRadius:9999, fontSize:'0.7rem', fontWeight:700 }}>🔁 Regression</span>}
                {detail.sla_breached  && <span style={{ background:'#FEF2F2', color:'#DC2626', padding:'1px 7px', borderRadius:9999, fontSize:'0.7rem', fontWeight:700 }}>⚠ SLA Breached</span>}
              </div>
              <h3 style={{ margin:0, fontSize:'0.95rem', fontWeight:800, color:'#111827' }}>{detail.title}</h3>
              <p style={{ margin:'0.2rem 0 0', fontSize:'0.72rem', color:'#9CA3AF' }}>
                #{detail.id} · Reported {fmt(detail.created_at)} · Reopened {detail.reopened_count}x
              </p>
            </div>
            <div style={{ display:'flex', gap:'0.35rem', marginLeft:'0.5rem' }}>
              <button onClick={() => setEditing(true)}
                style={{ background:'#EFF6FF', color:'#1D4ED8', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.72rem', fontWeight:600 }}>Edit</button>
              <button onClick={onClose}
                style={{ background:'#F3F4F6', border:'none', borderRadius:8, width:30, height:30, cursor:'pointer', color:'#6B7280', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
            </div>
          </div>

          {/* Quick status transitions */}
          {NEXT_STATES[detail.status]?.length > 0 && (
            <div style={{ marginTop:'0.625rem', display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
              <span style={{ fontSize:'0.7rem', color:'#9CA3AF', alignSelf:'center' }}>Move to:</span>
              {(NEXT_STATES[detail.status] || []).map(s => (
                <button key={s} onClick={() => changeStatus(s)}
                  style={{ padding:'2px 10px', border:`1px solid ${ST[s]?.color || '#E5E7EB'}20`,
                           background: ST[s]?.bg || '#F3F4F6', color: ST[s]?.color || '#6B7280',
                           borderRadius:9999, fontSize:'0.7rem', fontWeight:600, cursor:'pointer' }}>
                  {ST[s]?.icon} {ST[s]?.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sub-tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #F1F5F9', background:'#FAFAFA', padding:'0 1.25rem' }}>
          {[['details','Details'],['comments',`Comments${comments.length ? ` (${comments.length})` : ''}`],['activity','Activity']].map(([k,l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              style={{ padding:'0.45rem 0.875rem', border:'none', cursor:'pointer', fontSize:'0.8rem', fontWeight:600,
                       background:'transparent', color: tab===k ? theme.primary : '#6B7280',
                       borderBottom: tab===k ? `2px solid ${theme.primary}` : '2px solid transparent', transition:'all 0.12s' }}>
              {l}
            </button>
          ))}
        </div>

        <div style={{ flex:1, padding:'1.1rem 1.25rem', overflowY:'auto' }}>

          {/* ── Details tab ── */}
          {tab === 'details' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
              {detail.description && (
                <Section title="Description"><p style={{ margin:0, fontSize:'0.85rem', color:'#374151', lineHeight:1.6 }}>{detail.description}</p></Section>
              )}
              {detail.steps_to_reproduce && (
                <Section title="Steps to Reproduce">
                  <pre style={{ margin:0, fontSize:'0.82rem', color:'#374151', fontFamily:'monospace', whiteSpace:'pre-wrap', background:'#F9FAFB', padding:'0.75rem', borderRadius:8 }}>{detail.steps_to_reproduce}</pre>
                </Section>
              )}
              {(detail.expected_result || detail.actual_result) && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                  {detail.expected_result && <Section title="✅ Expected"><p style={{ margin:0, fontSize:'0.82rem', color:'#166534', background:'#F0FDF4', padding:'0.5rem 0.75rem', borderRadius:8 }}>{detail.expected_result}</p></Section>}
                  {detail.actual_result   && <Section title="❌ Actual"><p style={{ margin:0, fontSize:'0.82rem', color:'#991B1B', background:'#FFF1F2', padding:'0.5rem 0.75rem', borderRadius:8 }}>{detail.actual_result}</p></Section>}
                </div>
              )}
              <Section title="Details">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.4rem 1rem' }}>
                  <InfoRow label="Environment"   value={detail.environment?.charAt(0).toUpperCase() + (detail.environment?.slice(1) || '') || '—'} />
                  <InfoRow label="Browser/Device" value={detail.browser_device || '—'} />
                  <InfoRow label="Build"          value={detail.build_version  || '—'} />
                  <InfoRow label="Fix Version"    value={detail.fix_version    || '—'} />
                  <InfoRow label="Root Cause"     value={detail.root_cause ? detail.root_cause.replace(/_/g,' ') : '—'} />
                  <InfoRow label="Reporter"       value={detail.reporter_name  || '—'} />
                  <InfoRow label="Assignee"       value={detail.assignee_name  || 'Unassigned'} />
                  <InfoRow label="QA Assignee"    value={detail.qa_assignee_name || 'Unassigned'} />
                  {detail.pm_story_title && <InfoRow label="Story" value={detail.pm_story_title} />}
                  {detail.sla_deadline && <InfoRow label="SLA Deadline" value={fmtTime(detail.sla_deadline)} />}
                  {detail.duplicate_of_id && <InfoRow label="Duplicate Of" value={`Bug #${detail.duplicate_of_id}`} />}
                </div>
              </Section>
            </div>
          )}

          {/* ── Comments tab ── */}
          {tab === 'comments' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {comments.length === 0 && <p style={{ color:'#9CA3AF', fontSize:'0.85rem', textAlign:'center', padding:'1.5rem 0' }}>No comments yet</p>}
              {comments.map(c => (
                <div key={c.id} style={{ background:'#F9FAFB', border:'1px solid #F1F5F9', borderRadius:10, padding:'0.75rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.35rem' }}>
                    <span style={{ fontWeight:700, fontSize:'0.82rem', color:'#111827' }}>{c.author_name}</span>
                    <span style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>{fmtTime(c.created_at)}</span>
                  </div>
                  <CommentBody body={c.body} />
                </div>
              ))}
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem', alignItems:'flex-end' }}>
                <MentionInput
                  value={comment}
                  onChange={setComment}
                  members={members}
                  style={{ ...inp, resize:'none' }}
                  placeholder="Add a comment… type @ to mention"
                />
                <button onClick={addComment} disabled={!comment.trim()}
                  style={{ padding:'0.5rem 0.875rem', background: comment.trim() ? theme.primary : '#E5E7EB',
                           color: comment.trim() ? '#fff' : '#9CA3AF', border:'none', borderRadius:8,
                           cursor: comment.trim() ? 'pointer' : 'not-allowed', fontWeight:700, fontSize:'0.82rem', flexShrink:0 }}>
                  Post
                </button>
              </div>
            </div>
          )}

          {/* ── Activity tab ── */}
          {tab === 'activity' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
              {activities.length === 0 && <p style={{ color:'#9CA3AF', fontSize:'0.85rem', textAlign:'center', padding:'1.5rem 0' }}>No activity yet</p>}
              {activities.map(a => (
                <div key={a.id} style={{ display:'flex', gap:'0.625rem', padding:'0.4rem 0', borderBottom:'1px solid #F9FAFB' }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background: a.action==='status_change' ? '#F97316' : a.action==='created' ? '#22C55E' : '#3B82F6', marginTop:6, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <span style={{ fontSize:'0.8rem', color:'#374151', fontWeight:600 }}>{a.actor_name}</span>
                    {' '}
                    <span style={{ fontSize:'0.78rem', color:'#6B7280' }}>
                      {a.action === 'created'       && 'reported this bug'}
                      {a.action === 'status_change' && <>{`changed status`} <b style={{color:ST[a.from_value]?.color}}>{ST[a.from_value]?.label||a.from_value}</b> → <b style={{color:ST[a.to_value]?.color}}>{ST[a.to_value]?.label||a.to_value}</b></>}
                      {a.action === 'updated'       && `updated ${a.field_name}`}
                      {a.action === 'reopened'      && 'reopened this bug'}
                      {a.action === 'comment'       && 'added a comment'}
                    </span>
                    <p style={{ margin:'0.1rem 0 0', fontSize:'0.7rem', color:'#9CA3AF' }}>{fmtTime(a.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <BugModal bug={detail} projectId={detail.project_id} members={members}
          onClose={() => setEditing(false)} onSaved={() => { load(); onUpdated(); setEditing(false); }} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p style={{ margin:'0 0 0.4rem', fontSize:'0.7rem', fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.05em' }}>{title}</p>
      {children}
    </div>
  );
}
function InfoRow({ label, value }) {
  return (
    <div>
      <span style={{ fontSize:'0.7rem', color:'#9CA3AF', display:'block' }}>{label}</span>
      <span style={{ fontSize:'0.82rem', color:'#111827', fontWeight:500 }}>{value}</span>
    </div>
  );
}

// ─── Analytics Panel ──────────────────────────────────────────────────────────
function AnalyticsPanel({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    pm.getBugAnalytics(projectId).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:'2rem', color:'#6B7280' }}>Loading analytics…</div>
    </div>
  );
  if (!data) return null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', backdropFilter:'blur(4px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:860, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ padding:'1rem 1.5rem', borderBottom:'1px solid #F1F5F9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:'#111827' }}>📊 Bug Analytics</h3>
          <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', fontSize:'1rem', color:'#6B7280', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>
        <div style={{ padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'1.25rem' }}>

          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(130px, 1fr))', gap:'0.75rem' }}>
            {[
              { label:'Total Bugs',    value:data.total,            color:'#374151', bg:'#F3F4F6' },
              { label:'Open',          value:data.open,             color:'#EA580C', bg:'#FFF7ED' },
              { label:'Closed',        value:data.closed,           color:'#16A34A', bg:'#F0FDF4' },
              { label:'SLA Breached',  value:data.sla_breached,     color:'#DC2626', bg:'#FEF2F2' },
              { label:'Regressions',   value:data.regression_count, color:'#854D0E', bg:'#FEF9C3' },
              { label:'SLA %',         value:`${data.sla_compliance}%`, color:'#1D4ED8', bg:'#EFF6FF' },
              { label:'Reopen Rate',   value:`${data.reopen_rate}%`,color:'#BE185D', bg:'#FDF2F8' },
              { label:'MTTR',          value:`${data.mttr_hours}h`, color:'#0891B2', bg:'#ECFEFF' },
            ].map((k,i) => (
              <div key={i} style={{ background:k.bg, borderRadius:10, padding:'0.75rem', textAlign:'center' }}>
                <p style={{ fontSize:'0.65rem', fontWeight:700, color:k.color, textTransform:'uppercase', letterSpacing:'0.05em', margin:'0 0 0.25rem' }}>{k.label}</p>
                <p style={{ fontSize:'1.4rem', fontWeight:800, color:k.color, margin:0 }}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
            <ChartCard title="By Severity">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.by_severity} margin={{top:4,right:8,bottom:4,left:-10}}>
                  <XAxis dataKey="name" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4,4,0,0]}>
                    {data.by_severity.map((_,i) => <Cell key={i} fill={PALETTE[i%PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="By Status">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.by_status.map(s=>({...s,name:ST[s.name]?.label||s.name}))}
                       cx="50%" cy="50%" innerRadius={45} outerRadius={72} dataKey="count" paddingAngle={2}>
                    {data.by_status.map((_,i) => <Cell key={i} fill={PALETTE[i%PALETTE.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="By Priority">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.by_priority} margin={{top:4,right:8,bottom:4,left:-10}}>
                  <XAxis dataKey="name" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4,4,0,0]}>
                    {data.by_priority.map((_,i) => <Cell key={i} fill={PALETTE[i%PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="By Assignee (Open Bugs)">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.by_assignee.slice(0,8)} layout="vertical" margin={{top:4,right:24,bottom:4,left:70}}>
                  <XAxis type="number" tick={{fontSize:10}} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={70} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#F43F5E" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Aging table */}
          {data.aging?.length > 0 && (
            <div>
              <p style={{ fontSize:'0.8rem', fontWeight:700, color:'#111827', margin:'0 0 0.5rem' }}>🕒 Oldest Open Bugs</p>
              <div style={{ borderRadius:10, border:'1px solid #F1F5F9', overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      {['#','Title','Severity','Days Open'].map(h => (
                        <th key={h} style={{ padding:'0.5rem 0.75rem', textAlign:'left', fontWeight:700, color:'#9CA3AF', fontSize:'0.7rem', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.aging.map((b,i) => (
                      <tr key={b.id} style={{ borderTop:'1px solid #F9FAFB', background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ padding:'0.5rem 0.75rem', fontFamily:'monospace', color:'#9CA3AF' }}>#{b.id}</td>
                        <td style={{ padding:'0.5rem 0.75rem', color:'#111827', fontWeight:500 }}>{b.title}</td>
                        <td style={{ padding:'0.5rem 0.75rem' }}>
                          <span style={{ background:SEV[b.severity]?.bg, color:SEV[b.severity]?.color, padding:'1px 7px', borderRadius:6, fontSize:'0.7rem', fontWeight:700 }}>{SEV[b.severity]?.label||b.severity}</span>
                        </td>
                        <td style={{ padding:'0.5rem 0.75rem', fontWeight:700, color: b.days > 7 ? '#DC2626' : b.days > 3 ? '#D97706' : '#6B7280' }}>{b.days}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div style={{ background:'#F9FAFB', border:'1px solid #F1F5F9', borderRadius:12, padding:'0.875rem 1rem' }}>
      <p style={{ margin:'0 0 0.625rem', fontSize:'0.8rem', fontWeight:700, color:'#374151' }}>{title}</p>
      {children}
    </div>
  );
}

// ─── Main Bugs component ──────────────────────────────────────────────────────
export default function Bugs({ project }) {
  const location = useLocation();
  const [bugs,     setBugs]     = useState([]);
  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [view,     setView]     = useState('list');     // list | kanban
  const [modal,    setModal]    = useState(null);       // null | 'new' | bug obj
  const [drawer,   setDrawer]   = useState(null);       // bug obj
  const [analytics,setAnalytics]= useState(false);
  const [search,   setSearch]   = useState('');
  const [fSev,     setFSev]     = useState('');
  const [fPri,     setFPri]     = useState('');
  const [fSta,     setFSta]     = useState('');
  const [fReg,     setFReg]     = useState(false);
  const [fSla,     setFSla]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fSev) params.severity = fSev;
      if (fPri) params.priority = fPri;
      if (fSta) params.status   = fSta;
      if (fReg) params.is_regression = true;
      if (fSla) params.sla_breached  = true;
      if (search) params.search = search;
      const [bRes, mRes] = await Promise.all([
        pm.getBugs(project.id, params),
        pm.getMembers(project.id),
      ]);
      setBugs(bRes.data   || []);
      setMembers(mRes.data || []);
    } catch {}
    setLoading(false);
  }, [project.id, fSev, fPri, fSta, fReg, fSla, search]);

  useEffect(() => { load(); }, [load]);

  // Auto-open bug from ?open=<bug_id> (notification deep link)
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (openId && bugs.length > 0 && !drawer) {
      const target = bugs.find(b => b.id === Number(openId));
      if (target) setDrawer(target);
    }
  }, [bugs, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (bug) => {
    if (!window.confirm(`Delete bug "${bug.title}"?`)) return;
    await pm.deleteBug(bug.id).catch(e => alert(e.response?.data?.detail || 'Failed'));
    load();
  };

  const openCount    = bugs.filter(b => !['closed'].includes(b.status)).length;
  const critCount    = bugs.filter(b => ['blocker','critical'].includes(b.severity)).length;
  const slaBreached  = bugs.filter(b => b.sla_breached).length;
  const doneCount    = bugs.filter(b => b.status === 'closed').length;
  const regCount     = bugs.filter(b => b.is_regression).length;

  const btnStyle = (active) => ({
    padding:'0.3rem 0.8rem', borderRadius:'999px', fontSize:'0.78rem', fontWeight:600,
    cursor:'pointer', transition:'all 0.12s', border:'none',
    background: active ? theme.primary : '#F1F5F9',
    color:      active ? '#fff'         : '#6B7280',
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem', padding:'1.25rem 1.5rem',
                  fontFamily:"'Inter',-apple-system,sans-serif" }}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <h2 style={{ margin:0, fontSize:'1.05rem', fontWeight:800, color:'#111827' }}>⬡ Bug Tracker</h2>
          <p style={{ margin:'2px 0 0', fontSize:'0.78rem', color:'#6B7280' }}>
            {bugs.length} total · {openCount} open · {doneCount} closed
            {slaBreached > 0 && <span style={{ color:'#DC2626', fontWeight:700 }}> · ⚠ {slaBreached} SLA breach</span>}
          </p>
        </div>
        <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', alignItems:'center' }}>
          <button onClick={() => setAnalytics(true)} style={{ ...btnStyle(false), padding:'0.4rem 0.9rem' }}>📊 Analytics</button>
          <div style={{ display:'flex', border:`1px solid #E5E7EB`, borderRadius:8, overflow:'hidden' }}>
            {[['list','☰'],['kanban','⊞']].map(([v,l]) => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding:'0.3rem 0.7rem', border:'none', cursor:'pointer', fontSize:'0.85rem',
                         background: view===v ? theme.primary : '#fff', color: view===v ? '#fff' : '#6B7280' }}>{l}</button>
            ))}
          </div>
          <button onClick={() => setModal('new')}
            style={{ padding:'0.45rem 1rem', background:'#F43F5E', color:'#fff', border:'none',
                     borderRadius:9, cursor:'pointer', fontWeight:700, fontSize:'0.82rem',
                     boxShadow:'0 2px 8px rgba(244,63,94,0.3)', display:'flex', alignItems:'center', gap:'0.3rem' }}>
            ⬡ Report Bug
          </button>
        </div>
      </div>

      {/* ── Quick stat pills ── */}
      {bugs.length > 0 && (
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          {[
            { label:'Open',       count:openCount,   color:'#EA580C', bg:'#FFF7ED', onClick:() => setFSta(fSta==='new'?'':'new') },
            { label:'Critical/Blocker', count:critCount, color:'#DC2626', bg:'#FEF2F2', onClick:() => setFSev(fSev==='critical'?'':'critical') },
            { label:'SLA Breach', count:slaBreached, color:'#DC2626', bg:'#FEF2F2', onClick:() => setFSla(!fSla) },
            { label:'Regression', count:regCount,    color:'#854D0E', bg:'#FEF9C3', onClick:() => setFReg(!fReg) },
            { label:'Closed',     count:doneCount,   color:'#16A34A', bg:'#F0FDF4', onClick:() => setFSta(fSta==='closed'?'':'closed') },
          ].map(s => (
            <button key={s.label} onClick={s.onClick}
              style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.25rem 0.75rem',
                       background:s.bg, color:s.color, border:`1px solid ${s.color}20`,
                       borderRadius:9999, fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
              <span>{s.count}</span> {s.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center',
                    background:'#fff', border:`1px solid #E5E7EB`, borderRadius:10, padding:'0.625rem 0.875rem' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bugs…"
          style={{ padding:'0.35rem 0.65rem', border:'1px solid #E5E7EB', borderRadius:7, fontSize:'0.8rem', outline:'none', width:180 }} />
        {[
          [fSev,setFSev,'All Severities', SEV_ORDER.map(s=>({v:s,l:SEV[s].label}))],
          [fPri,setFPri,'All Priorities', Object.entries(PRI).map(([k,v])=>({v:k,l:v.label}))],
          [fSta,setFSta,'All Statuses',   Object.entries(ST).map(([k,v])=>({v:k,l:v.label}))],
        ].map(([val,setter,placeholder,opts],i) => (
          <select key={i} value={val} onChange={e => setter(e.target.value)}
            style={{ padding:'0.35rem 0.6rem', border:`1px solid #E5E7EB`, borderRadius:7, fontSize:'0.78rem', background:'#fff', color:'#374151', cursor:'pointer', outline:'none' }}>
            <option value="">{placeholder}</option>
            {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
        {(fSev||fPri||fSta||fReg||fSla||search) && (
          <button onClick={() => { setFSev(''); setFPri(''); setFSta(''); setFReg(false); setFSla(false); setSearch(''); }}
            style={{ padding:'0.3rem 0.7rem', border:'1px solid #E5E7EB', borderRadius:7, fontSize:'0.78rem', background:'#fff', color:'#6B7280', cursor:'pointer' }}>
            ✕ Clear
          </button>
        )}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:'#9CA3AF' }}>{bugs.length} bugs</span>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {[1,2,3].map(i => <div key={i} style={{ height:64, background:'#F3F4F6', borderRadius:12, animation:'pulse 1.5s ease-in-out infinite', animationDelay:`${i*0.1}s` }} />)}
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && bugs.length === 0 && (
        <div style={{ textAlign:'center', padding:'4rem 2rem', background:'#fff', borderRadius:16, border:`1px solid #E5E7EB` }}>
          <div style={{ width:64, height:64, borderRadius:'50%', background:'#F0FDF4', fontSize:'1.75rem', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem', color:'#22C55E' }}>✓</div>
          <p style={{ margin:0, fontWeight:800, fontSize:'1rem', color:'#111827' }}>
            {(fSev||fPri||fSta||fReg||fSla||search) ? 'No bugs match your filters' : 'No bugs reported!'}
          </p>
          <p style={{ margin:'0.375rem 0 1.25rem', fontSize:'0.875rem', color:'#6B7280' }}>Great — this project looks clean.</p>
          {!(fSev||fPri||fSta||fReg||fSla||search) && (
            <button onClick={() => setModal('new')}
              style={{ padding:'0.5rem 1.25rem', background:'#F43F5E', color:'#fff', border:'none', borderRadius:9, cursor:'pointer', fontWeight:700 }}>
              Report First Bug
            </button>
          )}
        </div>
      )}

      {/* ── List View ── */}
      {!loading && bugs.length > 0 && view === 'list' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.375rem' }}>
          {bugs.map(bug => {
            const sm  = SEV[bug.severity] || SEV.major;
            const st  = ST[bug.status]    || ST.new;
            const pm_ = PRI[bug.priority] || PRI.medium;
            return (
              <div key={bug.id}
                style={{ background:'#fff', border:`1px solid #F1F5F9`, borderRadius:10,
                         padding:'0.75rem 1rem 0.75rem 0', display:'flex', alignItems:'center', gap:0,
                         cursor:'pointer', transition:'box-shadow 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                onClick={() => setDrawer(bug)}>
                {/* Severity stripe */}
                <div style={{ width:4, alignSelf:'stretch', background:sm.dot, flexShrink:0, marginRight:'0.875rem', borderRadius:'0 0 0 0' }} />
                {/* Severity badge */}
                <span style={{ background:sm.bg, color:sm.color, border:`1px solid ${sm.border}20`,
                               padding:'2px 8px', borderRadius:6, fontSize:'0.68rem', fontWeight:800,
                               letterSpacing:'0.04em', flexShrink:0, marginRight:'0.75rem' }}>{sm.label}</span>
                {/* Title */}
                <div style={{ flex:1, minWidth:0, marginRight:'0.75rem' }}>
                  <p style={{ margin:0, fontSize:'0.875rem', fontWeight:700, color:'#111827',
                               overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{bug.title}</p>
                  <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', marginTop:'0.2rem' }}>
                    {bug.pm_story_title && <span style={{ background:'#EFF6FF', color:'#1D4ED8', padding:'0 6px', borderRadius:4, fontSize:'0.65rem', fontWeight:600 }}>📎 {bug.pm_story_title}</span>}
                    {bug.is_regression && <span style={{ background:'#FEF9C3', color:'#854D0E', padding:'0 6px', borderRadius:4, fontSize:'0.65rem', fontWeight:700 }}>🔁 Regression</span>}
                    {bug.assignee_name && <span style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>👤 {bug.assignee_name}</span>}
                    {bug.environment   && <span style={{ fontSize:'0.7rem', color:'#9CA3AF', background:'#F3F4F6', padding:'0 5px', borderRadius:4 }}>{bug.environment}</span>}
                  </div>
                </div>
                {/* Badges */}
                <div style={{ display:'flex', alignItems:'center', gap:'0.35rem', flexShrink:0, marginRight:'0.5rem' }}>
                  <span style={{ background:st.bg, color:st.color, padding:'2px 8px', borderRadius:9999, fontSize:'0.7rem', fontWeight:600 }}>{st.icon} {st.label}</span>
                  <span style={{ color:pm_.color, fontSize:'0.7rem', fontWeight:700 }}>{pm_.icon}</span>
                  <SlaChip sla_remaining_min={bug.sla_remaining_min} sla_breached={bug.sla_breached} status={bug.status} />
                  <span style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>{fmt(bug.created_at)}</span>
                </div>
                {/* Actions */}
                <div style={{ display:'flex', gap:'0.25rem' }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setModal(bug)}
                    style={{ background:'#EFF6FF', color:'#1D4ED8', border:'none', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:'0.7rem', fontWeight:600 }}>Edit</button>
                  <button onClick={() => handleDelete(bug)}
                    style={{ background:'#FFF1F2', color:'#9F1239', border:'none', borderRadius:6, padding:'3px 6px', cursor:'pointer', fontSize:'0.75rem' }}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Kanban View ── */}
      {!loading && bugs.length > 0 && view === 'kanban' && (
        <div style={{ display:'flex', gap:'0.75rem', overflowX:'auto', paddingBottom:'1rem' }}>
          {Object.entries(ST).map(([status, meta]) => {
            const col = bugs.filter(b => b.status === status);
            return (
              <div key={status} style={{ minWidth:220, maxWidth:220, flexShrink:0 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                               padding:'0.5rem 0.75rem', background:meta.bg, borderRadius:'8px 8px 0 0',
                               borderBottom:`2px solid ${meta.color}30` }}>
                  <span style={{ fontSize:'0.78rem', fontWeight:700, color:meta.color }}>{meta.icon} {meta.label}</span>
                  <span style={{ background:`${meta.color}20`, color:meta.color, borderRadius:9999, padding:'0 6px', fontSize:'0.68rem', fontWeight:700 }}>{col.length}</span>
                </div>
                <div style={{ background:'#F8FAFC', border:'1px solid #F1F5F9', borderTop:'none', borderRadius:'0 0 8px 8px', minHeight:120, maxHeight:500, overflowY:'auto', display:'flex', flexDirection:'column', gap:'0.375rem', padding:'0.5rem' }}>
                  {col.map(bug => {
                    const sm = SEV[bug.severity] || SEV.major;
                    return (
                      <div key={bug.id} onClick={() => setDrawer(bug)}
                        style={{ background:'#fff', borderRadius:8, border:'1px solid #F1F5F9', padding:'0.625rem',
                                 cursor:'pointer', borderLeft:`3px solid ${sm.dot}`, transition:'box-shadow 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                        <p style={{ margin:'0 0 0.3rem', fontSize:'0.8rem', fontWeight:700, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{bug.title}</p>
                        <div style={{ display:'flex', gap:'0.3rem', flexWrap:'wrap' }}>
                          <span style={{ background:sm.bg, color:sm.color, padding:'0 5px', borderRadius:4, fontSize:'0.65rem', fontWeight:700 }}>{sm.label}</span>
                          {bug.sla_breached && <span style={{ background:'#FEF2F2', color:'#DC2626', padding:'0 5px', borderRadius:4, fontSize:'0.65rem', fontWeight:700 }}>⚠ SLA</span>}
                        </div>
                        {bug.assignee_name && <p style={{ margin:'0.25rem 0 0', fontSize:'0.68rem', color:'#9CA3AF' }}>👤 {bug.assignee_name}</p>}
                      </div>
                    );
                  })}
                  {col.length === 0 && <p style={{ fontSize:'0.75rem', color:'#D1D5DB', textAlign:'center', padding:'0.75rem 0' }}>Empty</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <BugModal bug={modal === 'new' ? null : modal} projectId={project.id} members={members}
          onClose={() => setModal(null)} onSaved={load} />
      )}
      {drawer && (
        <BugDrawer bug={drawer} members={members}
          onClose={() => setDrawer(null)} onUpdated={load} />
      )}
      {analytics && <AnalyticsPanel projectId={project.id} onClose={() => setAnalytics(false)} />}
    </div>
  );
}
